import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { t, lookups } from "../../content";
import { useLocale } from "../../locale";

import PageLayout from "../../components/layoutComponents/PageLayout";
import Loading from "../../components/reusableElementsComponents/Loading";
import { SalesSelect } from "../../components/formComponents/Inputs";
import { CircularProgress, Box, Grid, FormControlLabel, Checkbox, Typography, Alert, Divider, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";

import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";

import TalentsApiService from "../../TalentsApiService";

import {
  updateUserAssessmentReportsAndData,
} from "../../store/actions/userDataActions";

import MazhrModal from "../../components/reusableElementsComponents/MazhrModal";
import AdeptAspectsSection from "../../components/TalentMatchScoreComponents/AdeptAspectsSection";
import SalesStrengthsSection from "../../components/TalentMatchScoreComponents/SalesStrengthsSection";
import TeamStrengthsSection from "../../components/TalentMatchScoreComponents/TeamStrengthsSection";
import CandidateMotivationFactors from "../../components/talentsComponents/CandidateMotivationFactors";
import CandidateCompetences from "../../components/talentsComponents/CandidateCompetences";

import { Circle as CircleIcon } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber"

import 'react-circular-progressbar/dist/styles.css';
import {toast, ToastContainer} from "react-toastify";
import downloadableReport from '../../assets/images/icons/downloadable-report.svg';
import greenCheckMark from '../../assets/images/icons/checkGreen.svg';


const assessmentCodeConvert = {
  "work-personality-and-behaviour": 102,
  "values-and-motivation": 201,
  "verbal-reasoning": 302,
  "numerical-reasoning": 301,
  "deductive-reasoning": 344,
  "english": 370,
  "adept15": 390,
};

const instrumentIdToReportIdMap = {
  102: 102009,
  201: 201006,
  301: 301002,
  302: 302002,
  344: 344002,
  370: 370002,
  390: 390004
};

function instructionItems(instruction) {
  if (Array.isArray(instruction)) return instruction;
  if (!instruction) return [];
  return String(instruction)
    .split("-")
    .map((item) => item.trim())
    .filter(Boolean);
}

function Assessment() {
  let { assessmentPathName } = useParams();
  const { locale } = useLocale();

  const pageData = lookups.assessmentBySlug[assessmentPathName];
  const userCompletedAssessments =
    useSelector((state) => state.userData?.all?.completed_assessments) || null;

  const userData = useSelector((state) => state.userData?.all);

  const [languageChoice, setLanguageChoice] = useState(null);
  const [rememberChoice, setRememberChoice] = useState(false);
  const [assessmentCompleted, setAssessmentCompleted] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [openRetakeDialog, setOpenRetakeDialog] = useState(false);
  const [fetchingResults, setFetchingResults] = useState(false);

  const talentsServiceObj = useRef(new TalentsApiService()).current;
  const dispatch = useDispatch();

  useEffect(() => {
    setLanguageChoice((current) => {
      const code = current?.code ?? 2;
      return lookups.testLanguages.find((item) => item.code === code) || lookups.testLanguages[0];
    });
  }, [locale]);

  useEffect(() => {
    if (assessmentPathName && userCompletedAssessments?.length > 0) {
      let match = userCompletedAssessments?.find(
        (x) => x === assessmentCodeConvert[assessmentPathName]
      );
      if (match) {
        setAssessmentCompleted(true);
        const selectedReportId = instrumentIdToReportIdMap[match];
        setAssessment({instrument_id: match, report_id: selectedReportId});
      } else {
        setAssessmentCompleted(false);
      }
    }
  }, [assessmentPathName, userCompletedAssessments]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const statusParam = urlSearchParams.get("status");
    const validParam = urlSearchParams.get("valid");
    const instrumentIdParam = urlSearchParams.get("instr");

    if (statusParam === "4" && validParam === "1") {
      setFetchingResults(true);
      talentsServiceObj.FetchAssessmentsResults("AON", instrumentIdParam || assessmentCodeConvert[assessmentPathName])
          .then(res => {
            if (res.httpStatus === 200 && res.json && res.json?.data) {
              const skill_assessment = res.json?.data?.skill_assessment ? {...userData.skill_assessment, ...res.json?.data?.skill_assessment} : userData.skill_assessment;
              const completed_assessments = res.json?.data?.completed_assessments || userData.completed_assessments;
              dispatch(updateUserAssessmentReportsAndData({
                skill_assessment, completed_assessments
              }));
            }
          })
    } // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!fetchingResults) return;

    const intervalId = setInterval(() => {
      if (fetchingResults) {
        window.location.href = window.location.origin + window.location.pathname;
      }
      clearInterval(intervalId);
    }, 20000);

    return () => clearInterval(intervalId);
  }, [fetchingResults]);

  const handleSelectChange = (e) => {
    const { value } = e.target;
    setLanguageChoice(lookups.testLanguages.find((item) => item.title === value));
  };

  const handleCheckboxChange = (e) => {
    const { checked } = e.target;
    setRememberChoice(checked);
  };

  const handleStartTest = (providerName, instrumentId, languageId) => {
    talentsServiceObj
      .GetAssessmentUrl(
        providerName || "aon",
        instrumentId || 102,
        languageId || 2
      )
      .then((res) => {
        if (res.httpStatus === 200 && res.json && res.json.data) {
          const url = res.json.data?.url;
          if (url && url.includes("https://", 0)) {
            window.location.assign(url);
          } else {
            console.log("GetAssessmentUrl url is not valid  >> ", url);
          }
        } else {
          console.log("GetAssessmentUrl error  >> ", res);
        }
      });
  };

  const renderTestInstruction = (
    assessmentCompleted,
    instruction,
    duration,
    selectTestLanguage,
    startTestButtonText,
    rememberChoiceText,
    instrumentId,
    isFetchingResults,
  ) => {
    const items = instructionItems(instruction);
    if (isFetchingResults) {
      return (
          <div className='test-measure-importance-column' style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            paddingTop: '30px'
          }}>
            <Typography variant="h5" gutterBottom>
              {t('assessments.loadingResults')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {t('assessments.loadingResultsBody')}
            </Typography>
            <Box mt={2}>
              <CircularProgress/>
            </Box>
          </div>
      );
    }
    if (assessmentCompleted) {
      return (
        <div>
          <h3>{t('assessments.results')}</h3>
          {assessment?.instrument_id === 390 && userData?.adept_aspects_points?.length === 15 &&
              <AdeptAspectsSection data={userData}/>}

          {assessment?.instrument_id === 102 && userData?.team_strengths?.length === 9 &&
              <TeamStrengthsSection data={userData} />
          }

          {assessment?.instrument_id === 102 && userData?.sales_strengths?.competence?.length === 8 &&
              <SalesStrengthsSection data={userData} />
          }

          {assessment?.instrument_id === 102 &&
              <CandidateCompetences data={userData} />
          }

          {assessment?.instrument_id === 201 &&
            <CandidateMotivationFactors data={userData} />
          }

          {[301, 302, 344, 370].includes(assessment?.instrument_id) &&
            <h5>{t('assessments.overallScore')} <strong>{userData?.skill_assessment?.[assessment?.instrument_id]}</strong></h5>
          }
          <div className="test-instruction">
            <div style={{width: '100%', display: "flex", justifyContent: 'center', alignItems: 'center'}}>
              <button
                  className="mazhrBtn default-green white"
                  style={{width: '500px', backgroundColor: '#FFBB31'}}
                  onClick={() => setOpenRetakeDialog(true)}
              >
                <WarningAmberIcon style={{ marginRight: '8px' }} />
                {t('assessments.retakeAssessments')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
        <>
          <div className="test-instruction">
            <div className="test-instruction-column test-instruction-column-left">
              <p className="test-instruction-heading">
                {assessmentCompleted ? (
                    <h3>{t('assessments.retakeAssessments')}</h3>
                ) : (
                    <h3>{t('assessments.instructions')}</h3>
                )}{" "}
              {duration && (
                <span className="bold">
                  <AccessAlarmIcon /> {duration}
                </span>
              )}
            </p>
            <ul>
              {items.map((item, index) => (
                  <li key={index} className="test-instruction-list-item">
                    {item}
                  </li>
                ))}
            </ul>
          </div>

          <div className="test-instruction-column">
            <p className="bold">
              <h3>{selectTestLanguage || t('assessments.selectLanguage')}</h3>
            </p>
            <SalesSelect
              label=""
              options={lookups.testLanguages}
              handleChange={handleSelectChange}
              errorContent={t('validation.pleaseSelectGroup')}
              option={languageChoice?.title}
              inputProps={{ style: {} }}
            />
            <FormControlLabel
              className="test-instruction-select-language"
              control={
                <Checkbox
                  checked={rememberChoice}
                  name="remeber_language_choice"
                  onChange={handleCheckboxChange}
                />
              }
              label={
                <span>
                  {rememberChoiceText || t('assessments.rememberChoice')}
                </span>
              }
              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
            />

            {assessmentCompleted ? (
              <button
                className="mazhrBtn default-green white"
                style={{backgroundColor:'#FFBB31'}}
                onClick={() => setOpenRetakeDialog(true)}
              >
                <WarningAmberIcon style={{ marginRight: '8px' }} />
                {t('assessments.retakeAssessments')}
              </button>
            ) : (
              <button
                className="mazhrBtn default-green white"
                onClick={() =>
                  handleStartTest(
                    "aon",
                    instrumentId || assessmentCodeConvert[assessmentPathName],
                    languageChoice.code
                  )
                }
              >
                {startTestButtonText || t('assessments.startTest')}
              </button>
            )}
          </div>
        </div>
      </>
    );
  };

  if (pageData) {
    let {
      instrumentId,
      titleDesktop,
      whatItMeasures,
      instruction,
      duration,
      selectTestLanguage,
      rememberChoice: rememberChoiceText,
      startTestButtonText,
    } = pageData;

    const items = instructionItems(instruction);

    return (
      <>
        <div className="bg-cover" key={locale}>
          <PageLayout>
            <div className="assessment-page-wrapper">
              <ToastContainer containerId="assessment-page"/>
              <h1>{titleDesktop || t('assessments.listTitle')}</h1>
            </div>
          </PageLayout>
          <PageLayout>
            {userCompletedAssessments ? (
                <div className="assessment-page-wrapper">
                  {assessmentCompleted && (
                      <>
                        <h3>{t('assessments.alreadyCompleted')}
                          <img
                              src={greenCheckMark}
                              alt="Completed"
                              style={{ width: '24px', height: '24px', marginLeft: '10px', verticalAlign: 'middle' }}
                          />
                        </h3>

                        <AssessmentReport
                            title={t('assessments.downloadReportTitle')}
                            instrument_id={instrumentId || assessment.instrument_id}
                            languageOptions={lookups.reportLanguages[assessment.report_id]?.languages || lookups.testLanguages}
                            team_hash={userData?.linked_teams?.[0]?.hash || null}
                            pool_hash={userData?.linked_pools?.[0]?.hash || null}
                            toast={toast}
                        />
                        <hr/>
                      </>
                  )}

                  <div>
                    <div className='test-measure-importance-column'>
                      {whatItMeasures && (
                          <>
                            <h3>{t('assessments.whatItMeasures')}</h3>
                            <p>{whatItMeasures}</p>
                          </>
                      )}
                    </div>
                    
                  </div>

                  <hr/>

                  {renderTestInstruction(
                      assessmentCompleted,
                      instruction,
                      duration,
                      selectTestLanguage,
                      startTestButtonText,
                      rememberChoiceText,
                      instrumentId || assessment.instrument_id,
                      fetchingResults,
                  )}
                </div>
            ) : (
                <Loading sectionLoading containerHeight="50vh"/>
            )}
          </PageLayout>
          <MazhrModal
              show={openRetakeDialog}
              onHide={() => setOpenRetakeDialog(false)}
              header={<h3 style={{color: '#C13F8A', textAlign: 'left', margin: '0', padding: '0'}}>
                {t('assessments.retake')} <span className="bold">
                  <AccessAlarmIcon/> {duration}
                </span>
              </h3>}
          >
            <div className="test-instruction-column test-instruction-column-left">
              <Box pt={1} pl={3} pr={3} pb={3} style={{ textAlign: 'left' }}>
                <Box mb={3}>
                  <Typography variant="h5" className="test-instruction-heading">
                    {t('assessments.instructions')}
                  </Typography>
                  <List>
                    {items.length > 0
                        ? items.map((item, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <CircleIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={item} />
                            </ListItem>
                        ))
                        : (
                            <Typography variant="body2">
                              {t('assessments.noInstructions')}
                            </Typography>
                        )}
                  </List>
                </Box>

                <Box mb={3}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm="auto">
                      <Typography variant="h6">
                        {t('assessments.selectLanguage')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                      <SalesSelect
                          label=""
                          options={lookups.testLanguages}
                          handleChange={handleSelectChange}
                          errorContent={t('validation.selectValidOption')}
                          option={languageChoice?.title}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box>
                  <FormControlLabel
                      control={
                        <Checkbox
                            checked={rememberChoice}
                            name="remember_language_choice"
                            onChange={handleCheckboxChange}
                        />
                      }
                      label={
                        <Typography variant="subtitle1">
                          {t('assessments.rememberChoice')}
                        </Typography>
                      }
                      sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                  />
                </Box>
              </Box>
            </div>
            <Alert severity="warning" style={{backgroundColor: '#FDECEF', color: '#C13F8A'}}>
              <Typography variant="h6" style={{color: '#C13F8A'}}>
                <strong>{t('assessments.retakeWarning')}</strong>
              </Typography>
            </Alert>
            <Divider sx={{marginBottom: 2}}/>
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
              <button
                  className="mazhrBtn default-green-border"
                  onClick={() => setOpenRetakeDialog(false)}
              >
                {t('common.actions.cancel')}
              </button>
              <button
                  className="mazhrBtn default-green mar-20 left"
                  style={{backgroundColor:'#FFBB31'}}
                  onClick={() =>
                      handleStartTest(
                          "aon",
                          instrumentId || assessmentCodeConvert[assessmentPathName],
                          languageChoice.code
                      )
                  }
              >
                {t('assessments.retake')}
              </button>
            </div>
          </MazhrModal>
        </div>
      </>
    );
  } else {
    return <Loading/>;
  }
}

const AssessmentReport = ({ title, instrument_id, team_hash, pool_hash,languageOptions, toast}) => {
    const [language, setLanguage] = useState(2);
    const [downloadLink, setDownloadLink] = useState(null);
    const [generateReportVisible, setGenerateReportVisible] = useState(true);

    const talentServiceObj = useMemo(() => {
        return new TalentsApiService();
    }, []);

    const handleSelectLanguageChange = (e) => {
        setLanguage(e.target.value);
        setGenerateReportVisible(true);
        setDownloadLink(null);
    };

    const selectedLanguageTitle = languageOptions.find(lang => lang.code.toString() === language.toString())?.title;

    const getDownloadLink = async () => {
        const toastId = toast.loading(t('talents.toasts.generatingReport'), {containerId: "assessment-page"});
        try {
            const response = await talentServiceObj.LoadAssessmentReport({
                instrument_id: instrument_id,
                language_id: language,
                team_hash: team_hash || null,
                pool_hash: pool_hash || null,
            });
            if (response?.httpStatus === 200 && response?.json?.data) {
                setDownloadLink(response.json.data.download_link);
                setGenerateReportVisible(false);
                toast.update(toastId, {
                    render: t('talents.toasts.reportGenerated'),
                    type: "success",
                    isLoading: false,
                    autoClose: 3000,
                    containerId: "assessment-page"
                });
            } else if (response?.httpStatus === 404 && response?.json?.error === "No report found") {
                toast.update(toastId, {
                    render: t('assessments.reportUnavailable', { language: selectedLanguageTitle }),
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    containerId: "assessment-page"
                });
                setDownloadLink(null);
            } else {
                toast.update(toastId, {
                    render: t('talents.toasts.reportFailedRetry'),
                    type: "error",
                    isLoading: false,
                    autoClose: 3000,
                    containerId: "assessment-page"
                });
                setDownloadLink(null);
            }

        } catch (error) {
            console.error("Error fetching download link:", error);
            toast.update(toastId, {
                render: t('talents.toasts.reportFailed'),
                type: "error",
                isLoading: false,
                autoClose: 2000,
                containerId: "assessment-page"
            });
            setDownloadLink(null);
        }
    };

    return (
        <div className="flex">
            <div className="flex" style={{minWidth: "300px"}}>
                <img src={downloadableReport} alt={t('talents.downloadableReportAlt')} className="downloadable-report-icon" />
                <p className="l bold m-10">{title}</p>
            </div>
            <select className="language-select" name="report-language-select"
                style={{ 
                    height: "40px",
                    padding: "5px 30px 5px 10px",
                    margin: "0 10px",
                    boxSizing: "border-box",
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    background: `url('data:image/svg+xml;utf8,<svg fill="black" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/></svg>') no-repeat right 10px center`,
                    backgroundColor: "#fff",
                    backgroundSize: "32px",
                }}
                value={language} 
                onChange={handleSelectLanguageChange}
            >
                {languageOptions.map((option, index) => (
                    <option key={index} value={option.code}>
                        {option.title}
                    </option>
                ))}
            </select>
            {generateReportVisible && (<button className="mazhrBtn default-green m-10 right" onClick={getDownloadLink}>
                {t('assessments.generateReport')}
            </button>)}
            {downloadLink && (
                <a className="mazhrBtn default-green" href={downloadLink} target="_blank" rel="noreferrer">
                    {t('assessments.downloadIn', { language: selectedLanguageTitle })}
                </a>
            )}
        </div>
    );
}

export default Assessment;
